import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { 
  Bot, Send, User, Loader2, Sparkles, X, Minimize2,
  MessageCircle, AlertCircle, ChevronDown
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import api from '../../lib/api';

const SUGGESTED_QUERIES = [
  "How many projects are active?",
  "Which projects are delayed?",
  "Show me all schedules",
  "Which activities are critical?",
  "What is the overall progress?"
];

const AIAssistantWidget = () => {
  const { user, canAccessPage } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId] = useState(() => `widget-session-${Date.now()}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const isMobile = useIsMobile();

  // Check if user has access to AI assistant
  const hasAccess = canAccessPage('ai-assistant');

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      const scrollContainer = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Focus input when widget opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Handle sending a query
  const handleSendQuery = async (queryText = query) => {
    if (!queryText.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: queryText.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/ai-assistant/org-query', {
        query: queryText.trim(),
        session_id: sessionId
      });

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response.data.answer,
        dataSummary: response.data.data_summary,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('AI query failed:', err);
      setError(err.response?.data?.detail || 'Failed to get response');
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: err.response?.data?.detail || 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  // Handle keyboard submit
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuery();
    }
  };

  // Format message content
  const formatContent = (content) => {
    if (!content) return null;

    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    let listKey = 0;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${listKey++}`} className="list-disc list-inside space-y-0.5 my-1 text-xs">
            {currentList.map((item, i) => (
              <li key={i} className="text-slate-600">{item}</li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.match(/^\d+\./)) {
        currentList.push(trimmed.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, ''));
      } else {
        flushList();
        if (trimmed) {
          if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
            elements.push(
              <p key={index} className="font-medium text-slate-800 mt-2 mb-0.5 text-xs">
                {trimmed.replace(/\*\*/g, '')}
              </p>
            );
          } else {
            elements.push(
              <p key={index} className="text-xs text-slate-600 my-0.5">{trimmed}</p>
            );
          }
        }
      }
    });

    flushList();
    return elements;
  };

  // Don't render if user doesn't have access — or on mobile (<=768px), where the
  // floating button overlaps the Collaborator view's bottom-right and can
  // intercept taps on the Actual-date cells. The org-query assistant is a
  // desktop feature; superintendents don't need it in the field view.
  if (!hasAccess || isMobile) {
    return null;
  }

  return (
    <>
      {/* Floating Button - Always visible when widget is closed */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
          data-testid="ai-widget-trigger"
        >
          <MessageCircle className="h-6 w-6 group-hover:scale-110 transition-transform" />
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-blue-500 items-center justify-center">
              <Sparkles className="h-2.5 w-2.5 text-white" />
            </span>
          </span>
        </button>
      )}

      {/* Floating Widget Panel - Fixed position, anchored to bottom-right */}
      {isOpen && (
        <div 
          className={`fixed right-6 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${
            isMinimized 
              ? 'bottom-6 w-72 h-14' 
              : 'bottom-6 w-96 max-h-[calc(100vh-100px)]'
          }`}
          style={!isMinimized ? { height: 'min(520px, calc(100vh - 100px))' } : undefined}
          data-testid="ai-widget-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">AI Assistant</h3>
                {!isMinimized && (
                  <p className="text-[10px] text-blue-100">Organization insights</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="ai-widget-minimize"
              >
                {isMinimized ? (
                  <ChevronDown className="h-4 w-4 rotate-180" />
                ) : (
                  <Minimize2 className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="ai-widget-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content (hidden when minimized) */}
          {!isMinimized && (
            <div className="flex flex-col" style={{ height: 'calc(100% - 56px)' }}>
              {/* Messages Area */}
              <ScrollArea className="flex-1 p-3" ref={scrollRef}>
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-6">
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-3">
                      <Bot className="h-6 w-6 text-blue-500" />
                    </div>
                    <h4 className="text-sm font-medium text-slate-800 mb-1">
                      How can I help?
                    </h4>
                    <p className="text-xs text-slate-500 mb-4 px-4">
                      Ask about projects, schedules, activities, or organization metrics
                    </p>
                    
                    {/* Suggested Queries */}
                    <div className="flex flex-wrap gap-1.5 justify-center px-2">
                      {SUGGESTED_QUERIES.slice(0, 3).map((sq, index) => (
                        <button
                          key={index}
                          onClick={() => handleSendQuery(sq)}
                          className="text-[10px] px-2.5 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                        >
                          {sq}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex gap-2 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {message.type !== 'user' && (
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                            message.type === 'error' ? 'bg-red-100' : 'bg-blue-100'
                          }`}>
                            {message.type === 'error' ? (
                              <AlertCircle className="h-3 w-3 text-red-500" />
                            ) : (
                              <Bot className="h-3 w-3 text-blue-600" />
                            )}
                          </div>
                        )}
                        
                        <div className={`max-w-[80%] rounded-xl px-3 py-2 ${
                          message.type === 'user' 
                            ? 'bg-blue-600 text-white' 
                            : message.type === 'error'
                              ? 'bg-red-50 border border-red-200'
                              : 'bg-slate-100'
                        }`}>
                          {message.type === 'user' ? (
                            <p className="text-xs">{message.content}</p>
                          ) : (
                            <div className="max-w-none">
                              {formatContent(message.content)}
                            </div>
                          )}
                        </div>
                        
                        {message.type === 'user' && (
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="h-3 w-3 text-slate-600" />
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {loading && (
                      <div className="flex gap-2 justify-start">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                          <Bot className="h-3 w-3 text-blue-600" />
                        </div>
                        <div className="bg-slate-100 rounded-xl px-3 py-2">
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Thinking...
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
              
              {/* Input Area */}
              <div className="p-3 border-t border-slate-100">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    placeholder="Ask anything..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    className="flex-1 h-9 text-sm"
                    data-testid="ai-widget-input"
                  />
                  <Button
                    onClick={() => handleSendQuery()}
                    disabled={!query.trim() || loading}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white h-9 w-9 p-0"
                    data-testid="ai-widget-send"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {messages.length > 0 && (
                  <button
                    onClick={() => setMessages([])}
                    className="mt-2 text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    Clear conversation
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AIAssistantWidget;
